export type AppPlatformPresentation = 'shared' | 'apple' | 'android';
export type AppSurfaceDensity = 'compact' | 'comfortable' | 'relaxed';
export type AppSurfaceLevel = 'primary' | 'secondary';

type AppSurfaceProps = {
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: '$sm' | '$md' | '$lg' | '$xl' | '$pill' | number;
  borderTopLeftRadius?: '$sm' | '$md' | '$lg' | '$xl' | '$pill' | number;
  borderTopRightRadius?: '$sm' | '$md' | '$lg' | '$xl' | '$pill' | number;
  px?: '$xs' | '$sm' | '$md' | '$lg' | number;
  py?: '$xs' | '$sm' | '$md' | '$lg' | number;
  gap?: '$xs' | '$sm' | '$md' | '$lg' | number;
  minHeight?: number;
  shadowOpacity?: number;
  shadowRadius?: number;
  elevation?: number;
  borderBottomWidth?: number;
  borderBottomColor?: string;
  minWidth?: number;
};

function densitySpace(
  density: AppSurfaceDensity,
  values: {
    compact: '$xs' | '$sm' | '$md' | '$lg' | number;
    comfortable: '$xs' | '$sm' | '$md' | '$lg' | number;
    relaxed: '$xs' | '$sm' | '$md' | '$lg' | number;
  },
) {
  return values[density];
}

export function getAppCardPresentationProps(
  presentation: AppPlatformPresentation = 'shared',
  density: AppSurfaceDensity = 'comfortable',
  level: AppSurfaceLevel = 'primary',
): AppSurfaceProps {
  const isSecondary = level === 'secondary';

  return {
    backgroundColor: isSecondary ? '$mutedBackground' : '$cardBackground',
    borderColor: '$borderColor',
    borderWidth: 0,
    borderRadius: presentation === 'apple' ? '$lg' : presentation === 'android' ? '$sm' : '$md',
    px: densitySpace(density, {
      compact: '$xs',
      comfortable: '$sm',
      relaxed: '$md',
    }),
    py: density === 'compact' ? '$xs' : density === 'relaxed' ? '$sm' : 12,
    gap: densitySpace(density, {
      compact: '$xs',
      comfortable: '$xs',
      relaxed: '$sm',
    }),
    shadowOpacity: isSecondary ? 0 : presentation === 'apple' ? 0.06 : presentation === 'android' ? 0.1 : 0.08,
    shadowRadius: isSecondary ? 0 : presentation === 'apple' ? 20 : presentation === 'android' ? 18 : 24,
    elevation: isSecondary ? 0 : presentation === 'apple' ? 1 : presentation === 'android' ? 3 : 2,
  };
}

export function getAppButtonPresentationProps(
  presentation: AppPlatformPresentation = 'shared',
  density: AppSurfaceDensity = 'comfortable',
): AppSurfaceProps {
  return {
    borderRadius: presentation === 'apple' ? '$pill' : presentation === 'android' ? '$md' : '$sm',
    px: densitySpace(density, {
      compact: '$xs',
      comfortable: '$sm',
      relaxed: '$md',
    }),
  };
}

export function getAppPanelPresentationProps(
  presentation: AppPlatformPresentation = 'shared',
  density: AppSurfaceDensity = 'comfortable',
): AppSurfaceProps {
  return {
    borderRadius: presentation === 'apple' ? '$xl' : presentation === 'android' ? '$md' : '$lg',
    minHeight: density === 'compact' ? 116 : density === 'relaxed' ? 148 : 132,
    px: densitySpace(density, {
      compact: '$xs',
      comfortable: '$sm',
      relaxed: '$md',
    }),
    py: density === 'compact' ? '$xs' : density === 'relaxed' ? '$sm' : 12,
  };
}

export function getAppListRowPresentationProps(
  presentation: AppPlatformPresentation = 'shared',
  density: AppSurfaceDensity = 'comfortable',
): AppSurfaceProps {
  return {
    gap: presentation === 'apple' || presentation === 'android' ? '$md' : '$sm',
    py: densitySpace(density, {
      compact: '$xs',
      comfortable: '$sm',
      relaxed: '$md',
    }),
    borderBottomColor: '$borderColor',
  };
}

export function getAppTabBarPresentationProps(
  presentation: AppPlatformPresentation = 'shared',
  density: AppSurfaceDensity = 'comfortable',
): AppSurfaceProps {
  return {
    backgroundColor: presentation === 'android' ? '$mutedBackground' : '$cardBackground',
    borderColor: '$borderColor',
    borderRadius: presentation === 'apple' ? '$lg' : presentation === 'android' ? '$xl' : 0,
    px: densitySpace(density, {
      compact: 4,
      comfortable: '$xs',
      relaxed: '$sm',
    }),
    py: presentation === 'android'
      ? densitySpace(density, {
          compact: 4,
          comfortable: 6,
          relaxed: 8,
        })
      : undefined,
    minWidth: 0,
  };
}

export function getAppTabButtonPresentationProps(
  presentation: AppPlatformPresentation = 'shared',
  density: AppSurfaceDensity = 'comfortable',
): AppSurfaceProps {
  return {
    borderRadius: presentation === 'apple' ? '$pill' : presentation === 'android' ? '$md' : 0,
    px: densitySpace(density, {
      compact: 4,
      comfortable: '$xs',
      relaxed: '$sm',
    }),
    py: densitySpace(density, {
      compact: '$xs',
      comfortable: '$sm',
      relaxed: '$md',
    }),
    minHeight:
      presentation === 'android'
        ? density === 'compact'
          ? 42
          : density === 'relaxed'
            ? 52
            : 46
        : density === 'compact'
          ? 40
          : density === 'relaxed'
            ? 48
            : 44,
  };
}

export function getAppBottomSheetPresentationProps(
  presentation: AppPlatformPresentation = 'shared',
): AppSurfaceProps {
  return {
    borderTopLeftRadius: presentation === 'android' ? '$lg' : '$xl',
    borderTopRightRadius: presentation === 'android' ? '$lg' : '$xl',
  };
}
